import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  TabPanel,
  TabPanels,
  Tabs,
  useMediaQuery,
} from '@chakra-ui/react';

import { FaTasks, FaBookReader, FaUsers, FaChevronDown } from 'react-icons/fa';
import { PiArticleMediumFill } from 'react-icons/pi';
import { MdOutlineEditNote, MdPayment } from 'react-icons/md';
import { TbBrandBlogger } from 'react-icons/tb';

import theme from 'ui/theme/theme';
import { AdminTabs } from './AdminTabsData';
import {
  ContentContainer,
  StyledTab,
  StyledTabList,
  StyledIconTab,
  LinkTab,
  ToolsSideNav,
  Wrapper,
} from 'pages/student/StudentProfile.styles';
import CenteredWrapper from 'ui/CenteredWrapper';
import AccessDenied from 'components/AccessDenied';

function SubTab({ icon, text, path, onClick, selected }) {
  function getFontWeight() {
    let fontWeight;
    if (selected) {
      fontWeight = 'bold';
    } else if (icon) {
      fontWeight = 'semibold';
    } else {
      fontWeight = 'unset';
    }
    return fontWeight;
  }

  return (
    <LinkTab to={`/admin/management/${path}`} selected={selected} icon={icon}>
      <StyledIconTab onClick={onClick} icon={icon}>
        {icon}
        <Text
          size="small"
          textDecoration={selected ? 'underlin' : 'none'}
          fontWeight={getFontWeight()}
          ml={icon ? '8px' : '28px'}
          minWidth="80px"
          _hover={{ fontWeight: 'bold' }}
        >
          {text}
        </Text>
      </StyledIconTab>
    </LinkTab>
  );
}

SubTab.propTypes = {
  icon: PropTypes.element,
  text: PropTypes.string,
  path: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

function IconTab({ icon, text, path, onClick, selected }) {
  function getFontWeight() {
    let fontWeight;
    if (icon) {
      fontWeight = 'semibold';
    } else {
      fontWeight = 'unset';
    }
    return fontWeight;
  }

  return (
    <LinkTab to={`/admin/management/${path}`} selected={selected} icon={icon}>
      <StyledIconTab onClick={onClick} icon={icon}>
        {icon}
        <Text
          size="small"
          fontWeight={getFontWeight()}
          ml={icon ? '8px' : '28px'}
          minWidth="80px"
        >
          {text}
        </Text>
      </StyledIconTab>
    </LinkTab>
  );
}

IconTab.propTypes = {
  icon: PropTypes.element,
  text: PropTypes.string,
  path: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

function ManagementCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const isAdmin = useSelector((state) => state.user.isAdmin);

  const [isMobile] = useMediaQuery();
  const [selectedTab, setSelectedTab] = useState('blogs');
  // const [selectedMenu, setSelectedMenu] = useState('');

  const path = '/admin/management';
  if (location.pathname === `${path}` || location.pathname === `${path}/`) {
    navigate(`${path}/blogs`);
  }

  const findPathEnd = (pathname) =>
    pathname.substring(pathname.lastIndexOf(path) + path.length + 1);
  const pathEnd = findPathEnd(location.pathname);

  const defaultIndex = AdminTabs.filter(
    (tab) => tab.name !== 'break'
  ).findIndex((tab) => pathEnd.includes(tab.link));
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  useEffect(() => {
    setSelectedTab(findPathEnd(location.pathname));
  }, [navigate, location.pathname]);

  if (!isAuthenticated || !isAdmin) {
    return <AccessDenied />;
  }

  function handleTabClick(tab) {
    setSelectedTab(tab.name);
    navigate(`${path}/${tab.link}`);
  }

  function handleTabSelection(tabLink) {
    navigate(`${path}/${tabLink}`);
    const currentTabIndex = AdminTabs.filter(
      (tab) => tab.name != 'break'
    ).findIndex((tab) => tab.link === tabLink);
    setSelectedIndex(currentTabIndex);
  }

  const MobileTabMenu = (
    <Flex
      w="calc(100% + 48px)"
      mx="-24px"
      boxShadow="0px 4px 15px -4px rgba(0, 0, 0, 0.15)"
    >
      <Menu placement="bottom">
        <MenuButton w="100%" h="48px" bg="gray.50">
          {AdminTabs.map(
            (tab, idx) =>
              selectedTab.includes(tab.link) && (
                <Flex
                  key={idx}
                  align="center"
                  justify="space-between"
                  px="24px"
                >
                  <Text>{tab.name}</Text>
                  <FaChevronDown size="16px" />
                </Flex>
              )
          )}
        </MenuButton>
        <MenuList border="0px" w="100vw">
          {AdminTabs.map(
            (tab) =>
              tab.name !== 'break' && (
                <MenuItem key={tab.link} px="24px">
                  <StyledTab
                    key={tab.key}
                    color={
                      selectedTab.includes(tab.name) &&
                      theme.colors.primary.default
                    }
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab.icon}
                    <Text
                      fontWeight={selectedTab.includes(tab.link) && 'bold'}
                      marginLeft="10px"
                      minWidth="80px"
                    >
                      {tab.name}
                    </Text>
                  </StyledTab>
                </MenuItem>
              )
          )}
        </MenuList>
      </Menu>
    </Flex>
  );

  return (
    <CenteredWrapper
      p={isMobile ? '0px' : '40px'}
      mt={isMobile ? '8px' : '32px'}
      mb={isMobile ? '12px' : '32px'}
    >
      <Tabs
        flexDirection={isMobile ? 'column' : 'row'}
        orientation="vertical"
        height="auto"
        index={selectedIndex}
      >
        {isMobile ? (
          MobileTabMenu
        ) : (
          <ToolsSideNav display={{ base: 'none', lg: 'block' }}>
            <Wrapper padding="24px 0px 8px 0px">
              <Text fontWeight="bold" mb="24px" pl="20px">
                MANAGEMENT
              </Text>
              <StyledTabList>
                <IconTab
                  icon={<TbBrandBlogger size="20px" />}
                  text="Blogs"
                  path="blogs"
                  onClick={() => handleTabSelection('blogs')}
                  selected={selectedTab === 'blogs'}
                />
                <IconTab
                  icon={<FaTasks size="20px" />}
                  text="Essay Prompts"
                  path="essay-prompt"
                  onClick={() => handleTabSelection('essay-prompt')}
                  selected={selectedTab === 'essay-prompt'}
                />
                <IconTab
                  icon={<PiArticleMediumFill size="20px" />}
                  text="Essays"
                  path="essay"
                  onClick={() => handleTabSelection('essay')}
                  selected={selectedTab === 'assay'}
                />
                <IconTab
                  icon={<MdOutlineEditNote size="20px" />}
                  text="Relay Prompts"
                  path="relay-prompt"
                  onClick={() => handleTabSelection('relay-prompt')}
                  selected={selectedTab === 'relay-prompt'}
                />
                <IconTab
                  icon={<FaBookReader size="20px" />}
                  text="Relay Stories"
                  path="story"
                  onClick={() => handleTabSelection('story')}
                  selected={selectedTab === 'story'}
                />
                <IconTab
                  icon={<MdPayment size="20px" />}
                  text="Payments"
                  path="payments"
                  onClick={() => handleTabSelection('payments')}
                  selected={selectedTab === 'payments'}
                />
                <IconTab
                  icon={<FaUsers size="20px" />}
                  text="Users"
                  path="users"
                  onClick={() => handleTabSelection('users')}
                  selected={selectedTab === 'users'}
                />
              </StyledTabList>
            </Wrapper>
          </ToolsSideNav>
        )}
        <ContentContainer>
          <TabPanels>
            {AdminTabs.map((tab) => (
              <TabPanel
                p={isMobile ? '0px' : '1rem'}
                key={`${tab.key}-tabpanel`}
              >
                {/* {dataInfo &&
                (Object.keys(dataInfo)[0] === 'DRAFT' || Object.keys(dataInfo)[0] === 'PENDING') ? (
                  <HeaderNav buttonInfos={buttonInfos} />
                ) : null} */}
                {tab.content}
              </TabPanel>
            ))}
          </TabPanels>
        </ContentContainer>
      </Tabs>
    </CenteredWrapper>
  );
}

export default ManagementCenter;
